import { MissionBadge } from '@/features/missions/components/MissionBadge'
import type { ProficiencyAssessmentFinalResult } from '@/features/nectar/api/models'
import { useTurnstileCaptcha } from '@/features/turnstile'
import { Button, Text } from '@/ui-kit'
import { Trans } from '@lingui/react/macro'
import { Check } from 'lucide-react'

type PhaseClaimRewardProps = {
  finalResult: ProficiencyAssessmentFinalResult
  claiming: boolean
  captchaEnabled: boolean
  onClaim: (turnstileToken?: string, callbacks?: { onCaptchaFailed?: () => void }) => void | Promise<void>
}

export const PhaseClaimReward = ({ finalResult, claiming, captchaEnabled, onClaim }: PhaseClaimRewardProps) => {
  const captcha = useTurnstileCaptcha({ enabled: captchaEnabled, action: 'submission' })

  const handleClaim = () => {
    onClaim(captcha.token ?? undefined, { onCaptchaFailed: captcha.reset })
  }

  return (
    <div
      className="mx-auto flex w-full max-w-[600px] flex-col items-center gap-6 rounded-2xl border border-solid border-gray-100 px-10 py-12 text-center"
      style={{ animation: 'nectar-slide-up 0.4s ease-out' }}
    >
      <div className="bg-green/10 flex size-16 items-center justify-center rounded-full">
        <Check className="text-green size-8" />
      </div>
      <div className="flex flex-col items-center gap-2">
        <Text type="h5">
          <Trans>Assessment Already Completed</Trans>
        </Text>
        <Text type="body2-regular" className="text-gray-80">
          <Trans>You already completed a proficiency assessment with result:</Trans>
        </Text>
      </div>
      <div className="flex flex-col items-center gap-2">
        <span className="text-h3 text-green block">{finalResult.level}</span>
        <MissionBadge variant="default">
          {finalResult.confidence === 'high'
            ? 'High confidence'
            : finalResult.confidence === 'medium'
              ? 'Medium confidence'
              : 'Low confidence'}
        </MissionBadge>
      </div>
      {captcha.renderWidget('w-full max-w-[300px]')}
      <Button
        onClick={handleClaim}
        isDisabled={claiming || (captcha.active && !captcha.token)}
        className="w-full max-w-[300px]"
      >
        {claiming ? 'Completing...' : 'Complete'}
      </Button>
    </div>
  )
}
